import { BufferAttribute, BufferGeometry, Color, DoubleSide, Group, Mesh, MeshLambertMaterial } from 'three';

/** Cross-section of a range, from the road outwards: distance from the road, and height as a share of the peak. */
const SECTION = [
  { x: 48, h: 0 },
  { x: 70, h: 0.3 },
  { x: 100, h: 0.65 },
  { x: 140, h: 1 },
  { x: 200, h: 0.75 },
  { x: 290, h: 0.4 },
] as const;
/** The far range across the end of the road: how far ahead, how wide, and how many points. */
const FAR = { ahead: 340, half: 380, points: 40, peak: 70 } as const;
/** Metres between points along the road, and how far the range reaches behind and ahead of the trotro. */
const STEP = 24;
const AHEAD = 380;
const BEHIND = 60;
const ROWS = Math.ceil((AHEAD + BEHIND) / STEP) + 1;
const PEAK = 80;
const FOOT = new Color('#2f5f2c');
const TOP = new Color('#6b9753');
const FAR_COLOUR = new Color('#587f5c');

/** A tall wobbly ridge line along the road, fixed to the ground (not to the trotro), so it can be driven past. */
function ridge(z: number, side: number): number {
  const a = Math.sin(z * 0.0071 + side * 1.7) * 0.5;
  const b = Math.sin(z * 0.0173 + side * 4.1) * 0.3;
  const c = Math.sin(z * 0.043 + side * 0.9) * 0.2;
  return 0.55 + 0.45 * (a + b + c) / 0.5;
}

/** Wooded hills on both sides of the road, rising and falling with how hilly the country is. */
export class Hills {
  readonly group = new Group();
  private readonly sides: { mesh: Mesh; geometry: BufferGeometry; side: number }[] = [];
  private readonly far: Mesh;
  private cell = Number.NaN;
  private shown = -1;

  constructor() {
    const material = new MeshLambertMaterial({ vertexColors: true, flatShading: true, side: DoubleSide });
    const farGeometry = new BufferGeometry();
    farGeometry.setAttribute('position', new BufferAttribute(new Float32Array(FAR.points * 2 * 3), 3));
    const farIndex: number[] = [];
    for (let i = 0; i < FAR.points - 1; i += 1) farIndex.push(i * 2, i * 2 + 1, i * 2 + 2, i * 2 + 1, i * 2 + 3, i * 2 + 2);
    farGeometry.setIndex(farIndex);
    this.far = new Mesh(farGeometry, new MeshLambertMaterial({ color: FAR_COLOUR, flatShading: true, side: DoubleSide }));
    this.far.frustumCulled = false;
    this.group.add(this.far);
    for (const side of [-1, 1]) {
      const geometry = new BufferGeometry();
      geometry.setAttribute('position', new BufferAttribute(new Float32Array(ROWS * SECTION.length * 3), 3));
      geometry.setAttribute('color', new BufferAttribute(new Float32Array(ROWS * SECTION.length * 3), 3));
      const index: number[] = [];
      for (let row = 0; row < ROWS - 1; row += 1) {
        for (let k = 0; k < SECTION.length - 1; k += 1) {
          const a = row * SECTION.length + k;
          const b = a + 1;
          const c = a + SECTION.length;
          const d = c + 1;
          index.push(a, b, c, b, d, c);
        }
      }
      geometry.setIndex(index);
      const mesh = new Mesh(geometry, material);
      mesh.frustumCulled = false;
      this.group.add(mesh);
      this.sides.push({ mesh, geometry, side });
    }
  }

  /** `busZ` is where the trotro is; `relief` (0–1) is how hilly it is there. */
  update(busZ: number, relief: number): void {
    this.group.visible = relief > 0.02;
    if (!this.group.visible) return;
    this.far.position.z = busZ - FAR.ahead;
    const cell = Math.floor(busZ / STEP);
    // Heights only change a little as relief moves, so the mesh is rebuilt when we cross a row or the relief changes noticeably.
    if (cell === this.cell && Math.abs(relief - this.shown) < 0.01) return;
    this.cell = cell;
    this.shown = relief;
    this.buildFar(relief);
    const colour = new Color();
    for (const { geometry, side } of this.sides) {
      const position = geometry.getAttribute('position') as BufferAttribute;
      const colours = geometry.getAttribute('color') as BufferAttribute;
      for (let row = 0; row < ROWS; row += 1) {
        const z = (cell - Math.ceil(AHEAD / STEP) + row) * STEP;
        const height = ridge(z, side) * PEAK * relief;
        SECTION.forEach((point, k) => {
          const i = row * SECTION.length + k;
          // Each point gets its own small bump so the sides look rocky rather than smooth.
          const bump = 1 + 0.18 * Math.sin(z * 0.31 + k * 2.3 + side);
          const y = point.h * height * bump - (k === 0 ? 2 : 0);
          position.setXYZ(i, side * point.x, y, z);
          colour.copy(FOOT).lerp(TOP, Math.min(1, y / (PEAK * 0.9)));
          colours.setXYZ(i, colour.r, colour.g, colour.b);
        });
      }
      position.needsUpdate = true;
      colours.needsUpdate = true;
      geometry.computeVertexNormals();
    }
  }

  /** The distant range: a row of peaks across the road ahead, fixed to the ground in x so it only drifts as relief changes. */
  private buildFar(relief: number): void {
    const position = this.far.geometry.getAttribute('position') as BufferAttribute;
    for (let i = 0; i < FAR.points; i += 1) {
      const x = -FAR.half + (2 * FAR.half * i) / (FAR.points - 1);
      const peak = (0.35 + 0.65 * Math.abs(Math.sin(x * 0.021 + 0.8) * 0.6 + Math.sin(x * 0.057 + 2.1) * 0.4)) * FAR.peak * relief;
      position.setXYZ(i * 2, x, -4, 0);
      position.setXYZ(i * 2 + 1, x, peak, 0);
    }
    position.needsUpdate = true;
    this.far.geometry.computeVertexNormals();
  }
}
